function escapeHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export function stripInlineMarkdown(value) {
  return String(value || '')
    .replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '$1')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1 ($2)')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/__([^_]+)__/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/_([^_]+)_/g, '$1')
    .replace(/~~([^~]+)~~/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();
}

const DISPLAY_HEADING_ALIASES = {
  answer: 'Answer',
  analysis: 'Analysis',
  authorities: 'Authorities',
  authority: 'Authority',
  background: 'Background',
  chronology: 'Chronology',
  conclusion: 'Conclusion',
  facts: 'Facts',
  holding: 'Holding',
  issue: 'Issue',
  issues: 'Issues',
  key_facts: 'Key Facts',
  key_points: 'Key Points',
  key_takeaways: 'Key Takeaways',
  missing_facts: 'Missing Facts',
  next_steps: 'Next Steps',
  observations: 'Observations',
  position: 'Position',
  questions: 'Questions',
  ratio: 'Ratio',
  recommendation: 'Recommendation',
  recommendations: 'Recommendations',
  relief: 'Relief',
  response: 'Response',
  risks: 'Risks',
  risks_and_gaps: 'Risks and Gaps',
  strengths: 'Strengths',
  strategy: 'Strategy',
  summary: 'Summary',
  timeline: 'Timeline',
  weaknesses: 'Weaknesses',
};

const GENERIC_DISPLAY_HEADINGS = new Set([
  'analysis',
  'answer',
  'response',
  'structured answer',
  'structured analysis',
  'structured response',
]);

const DISPLAY_TONE_MAP = {
  answer: 'summary',
  conclusion: 'summary',
  key_points: 'summary',
  key_takeaways: 'summary',
  position: 'summary',
  recommendation: 'summary',
  recommendations: 'summary',
  summary: 'summary',
  missing_facts: 'checklist',
  next_steps: 'checklist',
  questions: 'checklist',
  chronology: 'timeline',
  timeline: 'timeline',
  risks: 'warning',
  risks_and_gaps: 'warning',
  weaknesses: 'warning',
};

const CHATTER_PREFIXES = [
  /^certainly[!,.:\-\s]*/i,
  /^sure[!,.:\-\s]*/i,
  /^of course[!,.:\-\s]*/i,
  /^absolutely[!,.:\-\s]*/i,
];

function normalizeDisplayHeadingKey(value) {
  return stripInlineMarkdown(value)
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, '_');
}

function toDisplayHeading(value) {
  const raw = stripInlineMarkdown(value).replace(/[.:\s]+$/g, '').trim();
  if (!raw) return '';
  const headingKey = normalizeDisplayHeadingKey(raw);
  return DISPLAY_HEADING_ALIASES[headingKey] || raw;
}

function inferSectionTone(heading = '', fallback = 'default') {
  const headingKey = normalizeDisplayHeadingKey(heading);
  return DISPLAY_TONE_MAP[headingKey] || fallback;
}

function isGenericDisplayHeading(value) {
  const headingKey = normalizeDisplayHeadingKey(value);
  return GENERIC_DISPLAY_HEADINGS.has(headingKey);
}

function normalizeMarkdownLineSyntax(markdown) {
  return String(markdown || '')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .split('\n')
    .map((line) => line
      .replace(/^(\s*)(#{1,6})([^\s#])/, '$1$2 $3')
      .replace(/^(\s*)[•●▪◦]\s+/, '$1- ')
      .replace(/^(\s*)(\d+)\)\s+/, '$1$2. '))
    .join('\n')
    .trim();
}

function looksLikeDisplayHeading(label) {
  const raw = stripInlineMarkdown(label).replace(/[.:\s]+$/g, '').trim();
  if (!raw) return false;
  const headingKey = normalizeDisplayHeadingKey(raw);
  if (DISPLAY_HEADING_ALIASES[headingKey]) return true;

  const words = raw.split(/\s+/).filter(Boolean);
  if (words.length === 0 || words.length > 4 || raw.length > 34) return false;
  if (/[.!?]$/.test(raw)) return false;
  return raw === raw.toUpperCase() || /^[A-Z]/.test(raw);
}

function isChattyDisplayPreamble(text) {
  const normalized = stripInlineMarkdown(text).toLowerCase();
  if (!normalized || normalized.length > 140) return false;
  const hasChattyPrefix = CHATTER_PREFIXES.some((pattern) => pattern.test(normalized));
  if (normalized.endsWith(':')) {
    return (
      hasChattyPrefix
      || normalized.startsWith("here's")
      || normalized.startsWith('here is')
      || normalized.startsWith('below is')
      || normalized.startsWith('this is')
      || normalized.startsWith("let's")
      || normalized.startsWith('let us')
    );
  }
  return hasChattyPrefix;
}

function splitDisplayParagraph(text) {
  const paragraph = String(text || '').trim();
  if (!paragraph) return [];

  if (isChattyDisplayPreamble(paragraph)) {
    return [];
  }

  const boldOnlyMatch = paragraph.match(/^\*\*([^*:\n]{2,80})\*\*:?\s*$/);
  if (boldOnlyMatch && looksLikeDisplayHeading(boldOnlyMatch[1])) {
    return [{ type: 'heading', level: 2, text: toDisplayHeading(boldOnlyMatch[1]) }];
  }

  const plainHeadingOnly = stripInlineMarkdown(paragraph);
  if (/^[A-Za-z][A-Za-z0-9/&()' -]{2,60}:$/.test(plainHeadingOnly) && looksLikeDisplayHeading(plainHeadingOnly)) {
    return [{ type: 'heading', level: 2, text: toDisplayHeading(plainHeadingOnly) }];
  }

  const boldInlineMatch = paragraph.match(/^\*\*([^*:\n]{2,80})\*\*:\s+(.+)$/);
  if (boldInlineMatch && looksLikeDisplayHeading(boldInlineMatch[1])) {
    return [
      { type: 'heading', level: 2, text: toDisplayHeading(boldInlineMatch[1]) },
      { type: 'paragraph', text: boldInlineMatch[2].trim() },
    ];
  }

  const plainInlineMatch = paragraph.match(/^([A-Za-z][A-Za-z0-9/&()' -]{2,48}):\s+(.+)$/);
  if (plainInlineMatch && looksLikeDisplayHeading(plainInlineMatch[1])) {
    return [
      { type: 'heading', level: 2, text: toDisplayHeading(plainInlineMatch[1]) },
      { type: 'paragraph', text: plainInlineMatch[2].trim() },
    ];
  }

  return [{ type: 'paragraph', text: paragraph }];
}

function normalizeDisplayBlocks(blocks) {
  const normalized = [];
  blocks.forEach((block) => {
    if (!block) return;
    if (block.type === 'heading') {
      const headingText = toDisplayHeading(block.text);
      if (!headingText) return;
      normalized.push({
        ...block,
        text: headingText,
      });
      return;
    }
    if (block.type === 'paragraph') {
      normalized.push(...splitDisplayParagraph(block.text));
      return;
    }
    if (block.type === 'list') {
      const items = (block.items || []).map((item) => String(item || '').trim()).filter(Boolean);
      if (!items.length) return;
      normalized.push({
        ...block,
        items,
      });
      return;
    }
    if (block.type === 'code' && String(block.text || '').trim()) {
      normalized.push(block);
    }
  });

  while (normalized.length) {
    const [first, second] = normalized;
    if (first?.type === 'paragraph' && second && isChattyDisplayPreamble(first.text)) {
      normalized.shift();
      continue;
    }
    if (first?.type === 'heading' && second && isGenericDisplayHeading(first.text)) {
      normalized.shift();
      continue;
    }
    break;
  }

  return normalized;
}

function renderInlineMarkdownToHtml(value) {
  let rendered = escapeHtml(value);
  rendered = rendered.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, (_match, alt, url) => {
    return `<span>${escapeHtml(alt || url)}</span>`;
  });
  rendered = rendered.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_match, label, url) => {
    return `<a href="${escapeHtml(url)}">${escapeHtml(label)}</a>`;
  });
  rendered = rendered.replace(/`([^`]+)`/g, '<code>$1</code>');
  rendered = rendered.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  rendered = rendered.replace(/__([^_]+)__/g, '<strong>$1</strong>');
  rendered = rendered.replace(/\*([^*]+)\*/g, '<em>$1</em>');
  rendered = rendered.replace(/_([^_]+)_/g, '<em>$1</em>');
  rendered = rendered.replace(/~~([^~]+)~~/g, '<s>$1</s>');
  return rendered;
}

export function parseMarkdownBlocks(markdown) {
  const lines = String(markdown || '').replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n');
  const blocks = [];
  let paragraphLines = [];
  let listBlock = null;
  let codeLines = [];
  let inCodeBlock = false;

  const flushParagraph = () => {
    if (!paragraphLines.length) return;
    const text = paragraphLines.join(' ').trim();
    if (text) blocks.push({ type: 'paragraph', text });
    paragraphLines = [];
  };

  const flushList = () => {
    if (!listBlock || !listBlock.items.length) return;
    blocks.push(listBlock);
    listBlock = null;
  };

  const flushCode = () => {
    if (!codeLines.length) return;
    blocks.push({ type: 'code', text: codeLines.join('\n').trimEnd() });
    codeLines = [];
  };

  lines.forEach((rawLine) => {
    const line = rawLine.replace(/\t/g, '  ');
    const trimmed = line.trim();

    if (/^```/.test(trimmed)) {
      flushParagraph();
      flushList();
      if (inCodeBlock) {
        flushCode();
        inCodeBlock = false;
      } else {
        inCodeBlock = true;
      }
      return;
    }

    if (inCodeBlock) {
      codeLines.push(rawLine);
      return;
    }

    if (!trimmed) {
      flushParagraph();
      flushList();
      return;
    }

    const headingMatch = trimmed.match(/^(#{1,6})\s+(.*)$/);
    if (headingMatch) {
      flushParagraph();
      flushList();
      blocks.push({
        type: 'heading',
        level: headingMatch[1].length,
        text: headingMatch[2].trim(),
      });
      return;
    }

    const bulletMatch = trimmed.match(/^[-*]\s+(.*)$/);
    if (bulletMatch) {
      flushParagraph();
      if (!listBlock || listBlock.ordered) {
        flushList();
        listBlock = { type: 'list', ordered: false, items: [] };
      }
      listBlock.items.push(bulletMatch[1].trim());
      return;
    }

    const orderedMatch = trimmed.match(/^(\d+)\.\s+(.*)$/);
    if (orderedMatch) {
      flushParagraph();
      if (!listBlock || !listBlock.ordered) {
        flushList();
        listBlock = { type: 'list', ordered: true, items: [] };
      }
      listBlock.items.push(orderedMatch[2].trim());
      return;
    }

    flushList();
    paragraphLines.push(trimmed);
  });

  flushParagraph();
  flushList();
  if (inCodeBlock) flushCode();

  return blocks;
}

export function buildStructuredMarkdownSections(markdown) {
  const normalizedMarkdown = normalizeMarkdownLineSyntax(markdown);
  const blocks = normalizeDisplayBlocks(parseMarkdownBlocks(normalizedMarkdown));

  if (!blocks.length) {
    return { title: '', sections: [] };
  }

  let title = '';
  const remainingBlocks = [...blocks];
  if (remainingBlocks[0]?.type === 'heading' && Number(remainingBlocks[0].level || 0) === 1) {
    title = toDisplayHeading(remainingBlocks.shift().text);
  }

  const sections = [];
  let currentSection = { heading: '', tone: 'default', blocks: [] };

  const flushSection = () => {
    const hasContent = currentSection.heading || currentSection.blocks.length;
    if (!hasContent) return;
    const nextSection = {
      ...currentSection,
      heading: currentSection.heading || '',
      tone: currentSection.tone || inferSectionTone(currentSection.heading || ''),
      blocks: [...currentSection.blocks],
    };

    if (!nextSection.heading && nextSection.blocks.length === 1 && nextSection.blocks[0].type === 'list') {
      nextSection.heading = nextSection.blocks[0].ordered ? 'Next Steps' : 'Key Points';
      nextSection.tone = inferSectionTone(nextSection.heading, 'summary');
    }

    sections.push(nextSection);
    currentSection = { heading: '', tone: 'default', blocks: [] };
  };

  remainingBlocks.forEach((block) => {
    if (block.type === 'heading') {
      flushSection();
      currentSection = {
        heading: toDisplayHeading(block.text),
        tone: inferSectionTone(block.text),
        blocks: [],
      };
      return;
    }
    currentSection.blocks.push(block);
  });

  flushSection();

  if (!sections.length && title) {
    sections.push({ heading: title, tone: inferSectionTone(title, 'summary'), blocks: [] });
    title = '';
  }

  if (sections.length === 1 && !sections[0].heading && title) {
    sections[0].heading = title;
    sections[0].tone = inferSectionTone(title, sections[0].tone);
    title = '';
  }

  return { title, sections };
}

export function markdownToPlainText(markdown) {
  return parseMarkdownBlocks(markdown)
    .map((block) => {
      if (block.type === 'heading') return stripInlineMarkdown(block.text);
      if (block.type === 'paragraph') return stripInlineMarkdown(block.text);
      if (block.type === 'code') return block.text;
      if (block.type === 'list') {
        return block.items
          .map((item, index) => `${block.ordered ? `${index + 1}.` : '-'} ${stripInlineMarkdown(item)}`)
          .join('\n');
      }
      return '';
    })
    .filter(Boolean)
    .join('\n\n')
    .trim();
}

export function markdownToHtml(markdown) {
  const body = parseMarkdownBlocks(markdown)
    .map((block) => {
      if (block.type === 'heading') {
        const level = Math.min(Math.max(Number(block.level || 2), 1), 6);
        return `<h${level}>${renderInlineMarkdownToHtml(block.text)}</h${level}>`;
      }
      if (block.type === 'paragraph') {
        return `<p>${renderInlineMarkdownToHtml(block.text)}</p>`;
      }
      if (block.type === 'code') {
        return `<pre><code>${escapeHtml(block.text)}</code></pre>`;
      }
      if (block.type === 'list') {
        const tag = block.ordered ? 'ol' : 'ul';
        const items = block.items.map((item) => `<li>${renderInlineMarkdownToHtml(item)}</li>`).join('');
        return `<${tag}>${items}</${tag}>`;
      }
      return '';
    })
    .join('\n');

  return body.trim();
}

/**
 * markdownToStyledHtml
 *
 * Produces a full, self-contained HTML document with embedded CSS suitable
 * for copy-paste into Word, printing, or browser-based PDF generation.
 *
 * ## headings become bold H2 elements with a bottom border.
 * **bold** text is preserved. Code blocks use a monospace pre style.
 */
export function markdownToStyledHtml(markdown, options = {}) {
  const { title = '', author = '', includeWrapper = true } = options;

  const bodyHtml = markdownToHtml(markdown);

  if (!includeWrapper) return bodyHtml;

  const metaHeader = [
    title && `<div class="doc-title">${escapeHtml(title)}</div>`,
    author && `<div class="doc-meta">Prepared by: ${escapeHtml(author)}</div>`,
  ]
    .filter(Boolean)
    .join('\n');

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8" />
<meta name="viewport" content="width=device-width, initial-scale=1.0" />
<title>${escapeHtml(title || 'Document')}</title>
<style>
  /* ── Reset & base ────────────────────────────────────────────── */
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body {
    font-family: 'Times New Roman', Times, serif;
    font-size: 11pt;
    line-height: 1.75;
    color: #1a1a1a;
    background: #fff;
    padding: 48pt 60pt;
    max-width: 760pt;
    margin: 0 auto;
  }
  /* ── Document header ─────────────────────────────────────────── */
  .doc-title {
    font-size: 18pt;
    font-weight: 700;
    margin-bottom: 4pt;
    color: #111;
    border-bottom: 2px solid #222;
    padding-bottom: 8pt;
  }
  .doc-meta {
    font-size: 9pt;
    color: #555;
    margin-bottom: 24pt;
  }
  /* ── Headings ────────────────────────────────────────────────── */
  h1 { font-size: 16pt; font-weight: 700; margin: 20pt 0 8pt; color: #111; border-bottom: 1.5px solid #ccc; padding-bottom: 4pt; }
  h2 { font-size: 14pt; font-weight: 700; margin: 18pt 0 7pt; color: #1a1a1a; border-bottom: 1px solid #ddd; padding-bottom: 3pt; }
  h3 { font-size: 12pt; font-weight: 700; margin: 14pt 0 5pt; color: #222; }
  h4, h5, h6 { font-size: 11pt; font-weight: 700; margin: 10pt 0 4pt; color: #333; }
  /* ── Body copy ───────────────────────────────────────────────── */
  p { margin: 0 0 8pt; text-align: justify; }
  strong { font-weight: 700; color: #111; }
  em { font-style: italic; }
  s { text-decoration: line-through; }
  a { color: #1d4ed8; text-decoration: underline; }
  code { font-family: 'Courier New', Courier, monospace; font-size: 9.5pt; background: #f4f4f4; padding: 1pt 4pt; border-radius: 3pt; }
  pre {
    font-family: 'Courier New', Courier, monospace;
    font-size: 9pt;
    background: #f4f4f4;
    border: 1px solid #ddd;
    border-radius: 4pt;
    padding: 10pt;
    margin: 10pt 0;
    white-space: pre-wrap;
    word-break: break-word;
  }
  pre code { background: none; padding: 0; }
  /* ── Lists ───────────────────────────────────────────────────── */
  ul, ol { margin: 6pt 0 8pt 20pt; }
  li { margin-bottom: 4pt; line-height: 1.65; }
  /* ── Print optimisation ──────────────────────────────────────── */
  @media print {
    body { padding: 0; }
    h1, h2, h3 { page-break-after: avoid; }
    pre, blockquote { page-break-inside: avoid; }
  }
</style>
</head>
<body>
${metaHeader}
${bodyHtml}
</body>
</html>`;
}
